import React, { useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    child_age: '',
    program: '',
    message: '',
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `[Âge enfant: ${formData.child_age || 'non renseigné'}, Programme: ${formData.program || 'non choisi'}] ${formData.message}`,
      });
      toast.success('Message envoyé avec succès ! Nous vous répondrons bientôt.');
      setFormData({ name: '', email: '', phone: '', child_age: '', program: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error("Erreur lors de l'envoi du message. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* HERO */}
      <section className="page-hero py-14 md:py-20" data-testid="contact-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center max-w-2xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="font-heading text-4xl sm:text-5xl font-black text-[#1B6FE3] mb-3">
              Contactez-nous
            </h1>
            <p className="text-base text-slate-600">
              Vous avez des questions sur nos programmes ? Vous souhaitez inscrire votre enfant ? N'hésitez pas à nous écrire.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="py-16 md:py-20 bg-white" data-testid="contact-content">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 max-w-5xl mx-auto">
            {/* FORM */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="tk-card" data-testid="contact-form">
                <h2 className="font-heading text-xl font-extrabold text-slate-900 mb-5">Envoyez-nous un message</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-semibold text-slate-700 mb-1.5 block">Nom complet *</Label>
                    <Input id="name" data-testid="input-name" required placeholder="Votre nom"
                      value={formData.name} onChange={(e) => handleChange('name', e.target.value)}
                      className="rounded-xl border-slate-200 bg-slate-50 px-4 py-3" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-1.5 block">Email *</Label>
                    <Input id="email" data-testid="input-email" type="email" required placeholder="votre@email.com"
                      value={formData.email} onChange={(e) => handleChange('email', e.target.value)}
                      className="rounded-xl border-slate-200 bg-slate-50 px-4 py-3" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 mb-1.5 block">Téléphone</Label>
                      <Input id="phone" data-testid="input-phone" type="tel" placeholder="+225 XX XX XX XX"
                        value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)}
                        className="rounded-xl border-slate-200 bg-slate-50 px-4 py-3" />
                    </div>
                    <div>
                      <Label htmlFor="child_age" className="text-sm font-semibold text-slate-700 mb-1.5 block">Âge de l'enfant</Label>
                      <Input id="child_age" data-testid="input-child-age" type="number" min="5" max="17" placeholder="Ex: 10"
                        value={formData.child_age} onChange={(e) => handleChange('child_age', e.target.value)}
                        className="rounded-xl border-slate-200 bg-slate-50 px-4 py-3" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Programme intéressé</Label>
                    <Select value={formData.program} onValueChange={(value) => handleChange('program', value)}>
                      <SelectTrigger data-testid="select-program" className="rounded-xl border-slate-200 bg-slate-50 px-4 py-3">
                        <SelectValue placeholder="Sélectionnez un programme" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kids-5-7">Kids Robotics (5-7 ans)</SelectItem>
                        <SelectItem value="neo-8-10">Néo Robotics (8-10 ans)</SelectItem>
                        <SelectItem value="ruby-11-13">Ruby Robotics (11-13 ans)</SelectItem>
                        <SelectItem value="archy-14-15">Archy Robotics (14-15 ans)</SelectItem>
                        <SelectItem value="elite-16-17">Elite Robotics (16-17 ans)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-sm font-semibold text-slate-700 mb-1.5 block">Message *</Label>
                    <Textarea id="message" data-testid="input-message" rows={5} required
                      value={formData.message} onChange={(e) => handleChange('message', e.target.value)}
                      className="rounded-xl border-slate-200 bg-slate-50 px-4 py-3" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="submit-contact"
                    className="w-full btn-primary justify-center py-4 text-base">
                    {loading ? 'Envoi en cours...' : 'Envoyer le message'}
                  </Button>
                </div>
              </form>
            </div>

            {/* INFO */}
            <div className="lg:col-span-2 space-y-5">
              <h2 className="font-heading text-xl font-extrabold text-slate-900 mb-3">Informations pratiques</h2>
              <div className="tk-card flex items-start gap-3">
                <div className="bg-blue-50 p-2.5 rounded-lg"><MapPin className="w-5 h-5 text-[#1B6FE3]" /></div>
                <div>
                  <h3 className="font-heading font-extrabold text-slate-900 text-sm">Adresse</h3>
                  <p className="text-slate-600 text-sm">Espace Orange, Air France 3, Bouaké Côte d'Ivoire</p>
                </div>
              </div>
              <div className="tk-card flex items-start gap-3">
                <div className="bg-blue-50 p-2.5 rounded-lg"><Phone className="w-5 h-5 text-[#1B6FE3]" /></div>
                <div>
                  <h3 className="font-heading font-extrabold text-slate-900 text-sm">Téléphone</h3>
                  <p className="text-slate-600 text-sm">+225 0718463949</p>
                </div>
              </div>
              <div className="tk-card flex items-start gap-3">
                <div className="bg-orange-50 p-2.5 rounded-lg"><Mail className="w-5 h-5 text-[#F97316]" /></div>
                <div>
                  <h3 className="font-heading font-extrabold text-slate-900 text-sm">Email</h3>
                  <p className="text-slate-600 text-sm">contact@ludotech-robotics.com</p>
                </div>
              </div>
              
              {/* Map */}
              <div className="tk-card p-0 overflow-hidden">
                <div className="bg-slate-100 p-4 border-b border-slate-200">
                  <p className="font-heading font-extrabold text-slate-900 text-sm">Ludotech Bouaké</p>
                  <p className="text-slate-600 text-xs">Espace Orange, Air France 3</p>
                </div>
                <div className="h-48">
                  <iframe
                    title="Carte de localisation"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3948.0!2d-5.0333!3d7.6833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zN8KwNDEnMDAuMCJOIDXCsDAyJzAwLjAiVw!5e0!3m2!1sfr!2sci!4v1234567890"
                    width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
